import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  FormFieldInput,
  FormFieldType,
  type FormField,
  type FormFieldInput as FormFieldInputT,
} from "@alight/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@alight/ui";
import { ArrowDown, ArrowUp, Lock, Pencil, Plus, RotateCcw } from "lucide-react";

/**
 * API base. The site is served from the same origin as the Worker (Vite proxies
 * `/api` in dev), so relative requests are correct.
 */
const API_BASE = "";

/** Builder targets — the dynamic forms backed by the form_fields table. */
const FORM_KEYS = [
  { key: "enrolment", label: "Enrolment" },
  { key: "holiday", label: "Holiday" },
  { key: "camp", label: "Camp" },
  { key: "camp_leader", label: "Camp leaders" },
  { key: "leader", label: "Leaders" },
  { key: "contact", label: "Contact" },
] as const;
type FormKey = (typeof FORM_KEYS)[number]["key"];

/** Field types whose values are picked from a fixed option list. (A checkbox
 *  is a single yes/no — it carries no option list.) */
const OPTION_TYPES = new Set<FormField["type"]>(["select"]);

/** Strip the server-owned id so a field round-trips through FormFieldInput. */
function toInput(f: FormField): FormFieldInputT {
  return {
    form_key: f.form_key,
    label: f.label,
    name: f.name,
    type: f.type,
    options: f.options,
    description: f.description,
    required: f.required,
    sort_order: f.sort_order,
    archived: f.archived,
  };
}

function bySortOrder(a: FormField, b: FormField): number {
  return a.sort_order - b.sort_order || a.id - b.id;
}

/** The Contact form's four essential questions. POST /api/contact requires all
 *  four, so they can be reworded, retyped and reordered — but not retired or
 *  made optional. Mirrors CORE_NAMES in `apps/site/src/routes/contact.tsx`. */
const CORE_CONTACT_NAMES = new Set(["name", "email", "subject", "message"]);

function isCoreField(formKey: FormKey, name: string): boolean {
  return formKey === "contact" && CORE_CONTACT_NAMES.has(name);
}

/** A question the public form asks that is NOT one of the rows below — so the
 *  host reads this screen as the whole form, not just the editable part. */
interface BuiltInRow {
  name: string;
  label: string;
  type: FormField["type"];
  required: boolean;
  note: string;
}

/**
 * The questions the registration journey asks BY ITSELF, ahead of the field
 * list. Mirrors the built-ins in `apps/site/src/routes/enrol.tsx` — keep the
 * two in step, since this screen only DESCRIBES what that page renders.
 *
 * A real field of the same name replaces its built-in (that is why the
 * Enrolment tab can list parent/child questions itself). The one exception is
 * the email: the journey's sign-in step owns the verified address, so a
 * `parent_email` field never reaches the public form at all.
 */
function builtInsFor(formKey: FormKey, fieldNames: Set<string>): BuiltInRow[] {
  if (formKey === "contact") return [];
  const holiday = formKey === "holiday";
  /* The leader form registers the person filling it in, so the two built-in
     identity fields are THEIRS — and there is no separate child to name. */
  const leader = formKey === "leader";
  const asked = "Asked by the registration journey, ahead of the fields below.";
  const rows: BuiltInRow[] = [
    {
      name: "parent_email",
      label: "Email address",
      type: "email",
      required: true,
      note: "Verified at the sign-in step. A field named parent_email is ignored.",
    },
    {
      name: "parent_name",
      label: leader ? "Your full name" : "Parent / guardian name",
      type: "text",
      required: true,
      note: asked,
    },
    holiday
      ? {
          name: "parent_phone",
          label: "Your mobile (primary emergency contact)",
          type: "tel",
          required: true,
          note: "Required on holiday programs — it is the primary emergency contact for the day.",
        }
      : leader
        ? {
            name: "parent_phone",
            label: "Your mobile",
            type: "tel",
            required: true,
            note: "Required for leaders — we have to be able to reach you during the program.",
          }
        : { name: "parent_phone", label: "Parent / guardian phone", type: "tel", required: false, note: asked },
    ...(leader
      ? []
      : ([
          { name: "child_name", label: "Child's name", type: "text", required: true, note: asked },
        ] as BuiltInRow[])),
  ];
  if (!holiday && !leader) {
    rows.push({
      name: "year_level",
      label: "Year level",
      type: "select",
      required: true,
      note: "Term classes only — holiday programs ask a date of birth instead.",
    });
  }
  return rows.filter((r) => r.name === "parent_email" || !fieldNames.has(r.name));
}

export function AdminForms() {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const qc = useQueryClient();
  const [formKey, setFormKey] = React.useState<FormKey>("enrolment");
  const [editing, setEditing] = React.useState<FormField | null>(null);
  const [creating, setCreating] = React.useState(false);

  const queryKey = ["admin", "forms", formKey] as const;
  const fieldsQuery = useQuery({
    queryKey,
    queryFn: () => api.adminListForms(formKey),
  });

  const invalidate = React.useCallback(
    () => qc.invalidateQueries({ queryKey: ["admin", "forms", formKey] }),
    [qc, formKey],
  );

  const upsert = useMutation({
    mutationFn: ({ input, id }: { input: FormFieldInputT; id?: number }) =>
      api.adminUpsertForm(input, id),
    onSuccess: invalidate,
  });

  const retire = useMutation({
    mutationFn: (id: number) => api.adminDeleteForm(id),
    onSuccess: invalidate,
  });

  const all = fieldsQuery.data ?? [];
  const active = React.useMemo(
    () => all.filter((f) => f.archived === 0).sort(bySortOrder),
    [all],
  );
  const retired = React.useMemo(
    () => all.filter((f) => f.archived === 1).sort(bySortOrder),
    [all],
  );
  /* Only ACTIVE fields suppress a built-in — a retired field is gone from the
     public form, so its built-in comes back. */
  const builtIns = React.useMemo(
    () => builtInsFor(formKey, new Set(active.map((f) => f.name))),
    [formKey, active],
  );

  /**
   * Move the field at `index` by `delta` (-1 up, +1 down). Renumbers the active
   * list to a stable, gap-spaced sequence and upserts only the rows whose
   * sort_order actually changed (normally the two swapped neighbours).
   */
  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= active.length) return;
    const reordered = [...active];
    const [moved] = reordered.splice(index, 1);
    if (!moved) return;
    reordered.splice(target, 0, moved);
    reordered.forEach((field, i) => {
      const nextOrder = (i + 1) * 10;
      if (field.sort_order !== nextOrder) {
        upsert.mutate({ input: { ...toInput(field), sort_order: nextOrder }, id: field.id });
      }
    });
  }

  function restore(field: FormField) {
    upsert.mutate({ input: { ...toInput(field), archived: 0 }, id: field.id });
  }

  const busy = upsert.isPending || retire.isPending;

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-ink">Forms</h2>
          <p className="mt-1 text-sm text-muted">
            Build the public {FORM_KEYS.find((f) => f.key === formKey)?.label.toLowerCase()} form —
            add, reorder and retire fields. No code required.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} disabled={fieldsQuery.isLoading}>
          <Plus className="h-4 w-4" />
          Add field
        </Button>
      </header>

      <div className="flex gap-2 border-b border-border">
        {FORM_KEYS.map((f) => {
          const selected = f.key === formKey;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFormKey(f.key)}
              aria-pressed={selected}
              className={
                "-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors " +
                (selected
                  ? "border-primary text-primary"
                  : "border-transparent text-muted hover:text-ink")
              }
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {fieldsQuery.isSuccess && (builtIns.length > 0 || formKey === "contact") && (
        <p className="flex items-start gap-2 text-sm text-muted">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {formKey === "contact"
              ? "The four questions marked Essential are needed on every message, so they can’t be retired or made optional — but you can reword, reorder and retype them, and add your own questions alongside."
              : "Greyed rows are asked automatically by the registration journey, so they can’t be edited here. Add a field with the same data key to take one over."}
          </span>
        </p>
      )}

      {fieldsQuery.isLoading ? (
        <p className="text-sm text-muted">Loading fields…</p>
      ) : fieldsQuery.isError ? (
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-ink">
              Couldn’t load fields.{" "}
              {fieldsQuery.error instanceof ApiError
                ? fieldsQuery.error.message
                : "Please retry."}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => fieldsQuery.refetch()}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Order</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Options</TableHead>
                  <TableHead>Required</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {builtIns.map((row) => (
                  <TableRow key={`built-in-${row.name}`} className="bg-paper/60">
                    <TableCell>
                      <span className="flex h-8 w-8 items-center justify-center text-muted">
                        <Lock className="h-4 w-4" aria-hidden="true" />
                        <span className="sr-only">Always asked</span>
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-muted">{row.label}</span>
                      <p className="mt-0.5 text-xs text-muted">{row.note}</p>
                    </TableCell>
                    <TableCell>
                      <code className="rounded bg-card px-1.5 py-0.5 text-xs text-muted">
                        {row.name}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="muted">{row.type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted">—</TableCell>
                    <TableCell className="text-sm text-muted">
                      {row.required ? "Required" : "Optional"}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted">Always asked</TableCell>
                  </TableRow>
                ))}
                {active.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted">
                      {builtIns.length > 0
                        ? "No extra questions yet — this form asks only the built-in ones above."
                        : "No fields yet. Add the first one to build this form."}
                    </TableCell>
                  </TableRow>
                ) : (
                  active.map((field, i) => (
                    <TableRow key={field.id}>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Move ${field.label} up`}
                            disabled={i === 0 || busy}
                            onClick={() => move(i, -1)}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Move ${field.label} down`}
                            disabled={i === active.length - 1 || busy}
                            onClick={() => move(i, 1)}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-ink">{field.label}</TableCell>
                      <TableCell>
                        <code className="rounded bg-paper px-1.5 py-0.5 text-xs text-muted">
                          {field.name}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant="muted">{field.type}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[14rem] truncate text-sm text-muted">
                        {field.options.length > 0 ? field.options.join(", ") : "—"}
                      </TableCell>
                      <TableCell>
                        {field.required === 1 ? (
                          <Badge variant="accent">Required</Badge>
                        ) : (
                          <span className="text-sm text-muted">Optional</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Edit ${field.label}`}
                            disabled={busy}
                            onClick={() => setEditing(field)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {isCoreField(formKey, field.name) ? (
                            <span
                              className="self-center text-sm text-muted"
                              title="Every message needs this answer, so it can't be retired. You can still reword or reorder it."
                            >
                              Essential
                            </span>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={busy}
                              onClick={() => retire.mutate(field.id)}
                            >
                              Retire
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {retired.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Retired fields</CardTitle>
            <CardDescription>
              Hidden from the public form. Restore to bring a field back.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {retired.map((field) => (
              <div
                key={field.id}
                className="flex items-center gap-2 rounded-card border border-border bg-paper px-3 py-1.5"
              >
                <span className="text-sm text-ink">{field.label}</span>
                <code className="text-xs text-muted">{field.name}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  disabled={busy}
                  onClick={() => restore(field)}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Restore
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <FieldDialog
        key={editing ? `edit-${editing.id}` : creating ? "create" : "closed"}
        open={creating || editing !== null}
        formKey={formKey}
        field={editing}
        existingNames={all.map((f) => f.name)}
        pending={upsert.isPending}
        error={upsert.error instanceof ApiError ? upsert.error.message : null}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSubmit={(input) =>
          upsert.mutateAsync({ input, id: editing?.id }).then(() => {
            setCreating(false);
            setEditing(null);
          })
        }
        nextSortOrder={(active.length + 1) * 10}
      />
    </section>
  );
}

interface FieldDialogProps {
  open: boolean;
  formKey: FormKey;
  field: FormField | null;
  existingNames: string[];
  pending: boolean;
  error: string | null;
  nextSortOrder: number;
  onClose: () => void;
  onSubmit: (input: FormFieldInputT) => Promise<unknown>;
}

function FieldDialog({
  open,
  formKey,
  field,
  existingNames,
  pending,
  error,
  nextSortOrder,
  onClose,
  onSubmit,
}: FieldDialogProps) {
  const isEdit = field !== null;
  const [label, setLabel] = React.useState(field?.label ?? "");
  const [name, setName] = React.useState(field?.name ?? "");
  const [type, setType] = React.useState<FormField["type"]>(field?.type ?? "text");
  const [optionsText, setOptionsText] = React.useState((field?.options ?? []).join("\n"));
  const [description, setDescription] = React.useState(field?.description ?? "");
  const [required, setRequired] = React.useState((field?.required ?? 0) === 1);
  const [localError, setLocalError] = React.useState<string | null>(null);

  const needsOptions = OPTION_TYPES.has(type);
  /* A Contact essential must stay required — the endpoint rejects a blank one. */
  const core = isCoreField(formKey, name.trim());

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);

    const trimmedName = name.trim();
    if (!isEdit && existingNames.includes(trimmedName)) {
      setLocalError(`A field named "${trimmedName}" already exists.`);
      return;
    }

    const options = needsOptions
      ? optionsText
          .split("\n")
          .map((o) => o.trim())
          .filter(Boolean)
      : [];

    if (needsOptions && options.length === 0) {
      setLocalError("Add at least one option for select fields.");
      return;
    }

    const candidate: FormFieldInputT = {
      form_key: formKey,
      label: label.trim(),
      name: trimmedName,
      type,
      options,
      description: description.trim(),
      required: core || required ? 1 : 0,
      sort_order: field?.sort_order ?? nextSortOrder,
      archived: field?.archived ?? 0,
    };

    const parsed = FormFieldInput.safeParse(candidate);
    if (!parsed.success) {
      setLocalError(parsed.error.issues[0]?.message ?? "Please check the field values.");
      return;
    }

    void onSubmit(parsed.data);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit field" : "Add field"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update how this field appears on the public form."
              : "Define a new field for this form."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="field-label">Label</Label>
            <Input
              id="field-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Parent / guardian name"
              required
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="field-name">Name (data key)</Label>
            <Input
              id="field-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="parent_name"
              pattern="[a-z0-9_]+"
              title="Lowercase letters, numbers and underscores only"
              required
              disabled={isEdit}
            />
            <p className="text-xs text-muted">
              {isEdit
                ? "The data key is fixed once a field is created."
                : "Lowercase identifier used as the submission key."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="field-type">Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as FormField["type"])}>
                <SelectTrigger id="field-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FormFieldType.options.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary disabled:opacity-60"
                  checked={core || required}
                  disabled={core}
                  onChange={(e) => setRequired(e.target.checked)}
                />
                Required
                {core && <span className="text-xs text-muted">(essential)</span>}
              </label>
            </div>
          </div>

          {needsOptions && (
            <div className="space-y-1.5">
              <Label htmlFor="field-options">Options (one per line)</Label>
              <Textarea
                id="field-options"
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                placeholder={"Foundation\nYear 1\nYear 2"}
                rows={4}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="field-description">Helper text (optional)</Label>
            <Input
              id="field-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Shown in small print under the question"
            />
          </div>

          {(localError || error) && (
            <p className="text-sm text-accent-fg" role="alert">
              {localError ?? error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? "Saving…" : isEdit ? "Save changes" : "Add field"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
